import { useState, useEffect, useRef, useMemo } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import {
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/20/solid'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'

export interface SearchableSelectOption {
  id: string | number
  label: string
  subtitle?: string
  disabled?: boolean
}

interface DefaultItem {
  id: string | number
  name: string
  description?: string
  observations?: string
  code?: string
}

interface SearchableSelectProps {
  name: string
  fetcher: (_search: string) => Promise<SearchableSelectOption[]>
  value?: string | number | string[] | number[] | null
  onChange: (_value: string | number | null | (string | number)[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  label?: string
  disabled?: boolean
  className?: string
  error?: string
  required?: boolean
  multiple?: boolean
  defaultValue?: DefaultItem | DefaultItem[] | string | number | null
  dropdownPosition?: 'top' | 'bottom' | 'auto'
}

export function SearchableSelect({
  name,
  fetcher,
  value,
  onChange,
  placeholder = 'Seleccionar opción',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'No se encontraron resultados',
  label,
  disabled = false,
  className,
  error,
  required = false,
  multiple = false,
  defaultValue,
  dropdownPosition = 'bottom',
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOptionsState, setSelectedOptionsState] = useState<
    SearchableSelectOption | SearchableSelectOption[] | null
  >(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debouncedSearch = useDebounce(searchQuery, 400)

  const searchCacheRef = useRef<Map<string, SearchableSelectOption[]>>(
    new Map<string, SearchableSelectOption[]>()
  )

  useEffect(() => {
    searchCacheRef.current.clear()
    setSearchQuery('')
    ;(async () => {
      try {
        await refetch?.()
      } catch {
        // ignore
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  const fetchWithCache = async (
    search: string
  ): Promise<SearchableSelectOption[]> => {
    const cacheKey = `${name}:${search}`
    const cached = searchCacheRef.current.get(cacheKey)
    if (cached) {
      return cached
    }
    const result = await fetcher(search)
    searchCacheRef.current.set(cacheKey, result)
    return result
  }

  const {
    data: fetchedOptions = [],
    isLoading,
    refetch,
  } = useQuery<SearchableSelectOption[]>({
    queryKey: [name, debouncedSearch],
    queryFn: () => fetchWithCache(debouncedSearch),
    staleTime: 30 * 60 * 1000,
    enabled: !disabled,
  })

  useEffect(() => {
    if (!disabled && fetchedOptions.length === 0 && !isLoading) {
      refetch()
    }
  }, [disabled, fetchedOptions.length, isLoading, refetch])

  const defaultValueOptions = useMemo((): SearchableSelectOption[] => {
    if (!defaultValue) return []

    if (multiple && Array.isArray(defaultValue)) {
      return defaultValue.map((item) => ({
        id: item.id,
        label: item.name,
        subtitle: item.description || item.observations,
      }))
    } else if (
      !multiple &&
      typeof defaultValue === 'object' &&
      !Array.isArray(defaultValue) &&
      defaultValue.id &&
      defaultValue.name
    ) {
      return [
        {
          id: defaultValue.id,
          label: defaultValue.name,
          subtitle:
            defaultValue.description ||
            defaultValue.observations ||
            defaultValue.code,
        },
      ]
    }
    return []
  }, [defaultValue, multiple])

  const allOptions = useMemo((): SearchableSelectOption[] => {
    const combined: SearchableSelectOption[] = [...fetchedOptions]

    defaultValueOptions.forEach((defaultOption) => {
      if (
        !combined.some(
          (opt) => opt.id.toString() === defaultOption.id.toString()
        )
      ) {
        combined.unshift(defaultOption)
      }
    })

    return combined
  }, [fetchedOptions, defaultValueOptions])

  const options = useMemo((): SearchableSelectOption[] => {
    if (debouncedSearch.trim()) {
      return fetchedOptions
    }
    // Sin búsqueda, mostrar todas las opciones
    return allOptions
  }, [fetchedOptions, allOptions, debouncedSearch])

  // Update selected options state when value changes
  useEffect(() => {
    if (!value || value === 0 || (Array.isArray(value) && value.length === 0)) {
      setSelectedOptionsState(multiple ? [] : null)
      return
    }

    // Compute selected options from allOptions
    let computedSelectedOptions:
      | SearchableSelectOption
      | SearchableSelectOption[]
      | null
    if (multiple && Array.isArray(value)) {
      computedSelectedOptions = allOptions.filter((opt) =>
        (value as (string | number)[]).includes(opt.id)
      )
    } else if (!multiple && value !== null && value !== undefined) {
      computedSelectedOptions =
        allOptions.find((opt) => opt.id === value) || null
    } else {
      computedSelectedOptions = multiple ? [] : null
    }

    if (!computedSelectedOptions) {
      return
    }

    const currentIds = Array.isArray(selectedOptionsState)
      ? selectedOptionsState
          .map((opt) => opt.id)
          .sort()
          .join(',')
      : (selectedOptionsState?.id ?? '')

    const newIds = Array.isArray(computedSelectedOptions)
      ? computedSelectedOptions
          .map((opt) => opt.id)
          .sort()
          .join(',')
      : (computedSelectedOptions?.id ?? '')

    if (currentIds !== newIds) {
      setSelectedOptionsState(computedSelectedOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, multiple])

  const handleSelect = (option: SearchableSelectOption) => {
    if (multiple) {
      const currentValues = (Array.isArray(value) ? value : []) as (
        | string
        | number
      )[]
      if (currentValues.includes(option.id)) {
        const newValues = currentValues.filter((id) => id !== option.id)
        onChange(newValues)
        const newSelectedOptions = allOptions.filter((opt) =>
          newValues.includes(opt.id)
        )
        setSelectedOptionsState(newSelectedOptions)
      } else {
        const newValues = [...currentValues, option.id]
        onChange(newValues)
        const newSelectedOptions = allOptions.filter((opt) =>
          newValues.includes(opt.id)
        )
        setSelectedOptionsState(newSelectedOptions)
      }
    } else {
      onChange(option.id)
      setSelectedOptionsState(option)
      setIsOpen(false)
      setSearchQuery('')
    }
  }

  const isSelected = (option: SearchableSelectOption) => {
    if (multiple && Array.isArray(value)) {
      return (value as (string | number)[]).includes(option.id)
    }
    return value === option.id
  }

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {label && (
        <label
          className={cn(
            'mb-1 block text-sm font-medium',
            disabled ? 'text-gray-400' : 'text-gray-700'
          )}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <PopoverPrimitive.Root onOpenChange={setIsOpen} open={isOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            className={cn(
              'relative flex h-9 w-full items-center rounded-md border bg-neutral-50 pl-3 pr-10',
              'text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm',
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : disabled
                  ? 'cursor-not-allowed border-neutral-300 opacity-60'
                  : 'border-neutral-300 focus:border-neutral-500 focus:ring-neutral-500',
              disabled && 'cursor-not-allowed'
            )}
            disabled={disabled}
            type="button"
          >
            {multiple ? (
              <div className="flex flex-wrap gap-1">
                {Array.isArray(selectedOptionsState) &&
                selectedOptionsState.length > 0 ? (
                  selectedOptionsState.map((opt: SearchableSelectOption) => (
                    <span
                      className="rounded bg-neutral-200 px-2 py-0.5 text-xs"
                      key={opt.id}
                    >
                      {opt.label}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">{placeholder}</span>
                )}
              </div>
            ) : (
              <span
                className={cn(
                  'block truncate',
                  selectedOptionsState ? 'text-gray-900' : 'text-gray-500'
                )}
              >
                {selectedOptionsState
                  ? (selectedOptionsState as SearchableSelectOption).label
                  : placeholder}
              </span>
            )}

            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              ) : (
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-4 w-4 text-gray-400 opacity-100"
                />
              )}
            </span>
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            className={cn(
              'z-[9999] flex w-[--radix-popover-trigger-width] flex-col',
              'overflow-hidden rounded-[6px] border border-[#D4D4D4] bg-[#FAFAFA]',
              'shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
            )}
            onOpenAutoFocus={(e) => {
              e.preventDefault()
              searchInputRef.current?.focus()
            }}
            onWheel={(e) => {
              e.stopPropagation()
            }}
            side={dropdownPosition === 'top' ? 'top' : 'bottom'}
            sideOffset={4}
          >
            <div className="flex flex-row items-start gap-2 p-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#737373]"
                />
                <input
                  className={cn(
                    'h-9 min-h-[36px] w-full min-w-[200px] rounded-[6px]',
                    'border border-[#D4D4D4] bg-[#FAFAFA] px-4 py-0 pl-[44px]',
                    'text-sm font-medium leading-5 tracking-[0.005em] text-[#737373]',
                    'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]',
                    'placeholder:text-[#737373]',
                    'focus:border-[#D4D4D4] focus:outline-none focus:ring-0'
                  )}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                />
              </div>
            </div>

            <div className="flex h-px w-full items-center rounded-[2px] bg-[#FAFAFA]">
              <div className="h-px w-full bg-[#D4D4D4]" />
            </div>

            <ul
              className={cn(
                'max-h-[147px] flex-1',
                'overflow-y-auto p-2',
                'scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#737373]',
                'touch-pan-y'
              )}
              onTouchMove={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              style={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }}
            >
              {isLoading ? (
                <li className="flex items-center justify-center px-3 py-8">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#D4D4D4] border-t-[#737373]" />
                </li>
              ) : options.length === 0 ? (
                <li className="px-3 py-2 text-center text-sm text-[#737373]">
                  {emptyMessage}
                </li>
              ) : (
                options.map((option) => (
                  <li key={option.id}>
                    <button
                      className={cn(
                        'relative flex w-full cursor-pointer select-none items-center',
                        'gap-2 rounded-[6px] px-2 py-1 text-left transition-colors',
                        'min-h-[32px]',
                        option.disabled
                          ? 'cursor-not-allowed bg-[#FAFAFA] text-gray-400'
                          : 'bg-[#FAFAFA] text-[#262626] hover:bg-neutral-200'
                      )}
                      disabled={option.disabled}
                      onClick={() => !option.disabled && handleSelect(option)}
                      type="button"
                    >
                      {multiple && (
                        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                          <div
                            className={cn(
                              'flex h-4 w-4 items-center justify-center rounded border transition-colors',
                              isSelected(option)
                                ? 'border-slate-800 bg-slate-800'
                                : 'border-[#D4D4D4] bg-[#FAFAFA]'
                            )}
                          >
                            {isSelected(option) && (
                              <svg
                                className="h-3 w-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                viewBox="0 0 16 16"
                              >
                                <path
                                  d="M3.33337 8L6.66671 11.3333L12.6667 5.33331"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-1 flex-col justify-center">
                        <span
                          className={cn(
                            'block truncate text-sm font-normal leading-5',
                            'tracking-[0.005em] text-[#262626]',
                            !multiple && isSelected(option) && 'font-semibold'
                          )}
                        >
                          {option.label}
                        </span>
                        {option.subtitle && (
                          <span className="text-xs text-[#737373]">
                            {option.subtitle}
                          </span>
                        )}
                      </div>

                      {!multiple && isSelected(option) && (
                        <span className="flex items-center text-[#737373]">
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              clipRule="evenodd"
                              d={cn(
                                'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a',
                                '1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                              )}
                              fillRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
